import React, { useState } from 'react';
import { Figma, Check, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface FigmaCopyButtonProps {
  figmaHtmlData: string;
  title: string;
}

export default function FigmaCopyButton({ figmaHtmlData, title }: FigmaCopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleCopy = async () => {
    if (!figmaHtmlData) {
      toast.error('No Figma data available to copy');
      return;
    }

    try {
      setLoading(true);
      setError(false);

      // Check if ClipboardItem is supported
      if (typeof ClipboardItem !== 'undefined') {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([figmaHtmlData], { type: 'text/html' }),
          'text/plain': new Blob([`Figma component: ${title}`], { type: 'text/plain' })
        });

        await navigator.clipboard.write([clipboardItem]);
        console.log('Figma HTML data copied to clipboard');
      } else {
        // Fallback for browsers that don't support ClipboardItem
        await navigator.clipboard.writeText(figmaHtmlData);
        console.log('Figma data copied as text (fallback)');
      }

      setCopied(true);
      toast.success('Figma component copied! You can now paste it in Figma.');
      setTimeout(() => setCopied(false), 3000);

    } catch (err) {
      console.error('Failed to copy Figma data:', err);
      setError(true);
      toast.error('Failed to copy Figma component. Please try again.');
      setTimeout(() => setError(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleCopy}
      disabled={loading || !figmaHtmlData}
      className={`flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200 ${
        copied
          ? 'bg-green-100 text-green-600'
          : error
          ? 'bg-red-100 text-red-600'
          : loading
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-purple-100 text-purple-700 hover:bg-purple-500 hover:text-white'
      }`}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          <span>Copying...</span>
        </>
      ) : copied ? (
        <>
          <Check className="h-4 w-4 mr-1" />
          <span>Copied!</span>
        </>
      ) : error ? (
        <>
          <AlertCircle className="h-4 w-4 mr-1" />
          <span>Error</span>
        </>
      ) : (
        <>
          <Figma className="h-4 w-4 mr-1" />
          <span>Copy</span>
        </>
      )}
    </button>
  );
}